// @ts-nocheck
import InputLabelField from '@/components/form-control/InputLabelField'
import { useAuthContext } from '@/features/auth/context'
import { useConfigUI } from '@/features/config/context'
import useAddHydraulic from '@/features/config/hooks/hydraulic/useAddHydraulic'
import useEditHydraulic from '@/features/config/hooks/hydraulic/useEditHydraulic'
import useSearchHydraulicId from '@/features/config/hooks/hydraulic/useSearchHydraulicId'
import useTranslation from '@/hooks/useTranslation'
import { useUnits } from '@/hooks/useUnits'
import { FormPipe } from '@/types/hydraulic'
import { yupResolver } from '@hookform/resolvers/yup'
import { LoadingButton } from '@mui/lab'
import {
  Button,
  CircularProgress,
  Container,
  Fade,
  Stack,
  Typography,
} from '@mui/material'
import { useEffect, useRef } from 'react'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'
import classes from './style.module.scss'
import useUser from '@features/auth/hooks/useUser'

interface FormPipeProps {
  id?: number
  parentId?: number
}

const FormHydraulicView = ({ id, parentId }: FormPipeProps) => {
  const { t } = useTranslation()
  const { closeSidebar } = useConfigUI()
  const { interfaceId } = useAuthContext()
  const inputRefSiteName = useRef<HTMLInputElement>(null)
  const { convertLabelUnit, convertFromMetricV2, convertToMetricV2 } =
    useUnits()

  const { data: user } = useUser()
  const isMetric = !user?.measure?.id || user?.measure?.id === 1

  const maxCapacity =
    convertLabelUnit('l/m') === 'l/m' ? 399999999 : 105668820.94
  const maxCapacityMessage =
    convertLabelUnit('l/m') === 'l/m' ? '400,000,000' : '105668820.94'

  const schema = yup.object({
    description: yup
      .string()
      .trim()
      .max(
        32,
        `${t('commonLabelsDescription')} ${t('commonFormsValidationmustNotExceed32Character')}`,
      )
      .required(
        `${t('commonLabelsDescription')} ${t('commonFormsValidationIsRequired')}`,
      ),
    capacity: yup
      .number()
      .typeError(
        `${t('formsLabelsCapacity')} ${t('commonFormsValidationMustBeANumber')}`,
      )
      .min(
        0,
        `${t('formsLabelsCapacity')} ${t('commonFormsValidationCannotBeNegative')}`,
      )
      .max(
        maxCapacity,
        `${t('formsLabelsCapacity')} ${t('cannotExceed')} ${maxCapacityMessage} ${convertLabelUnit(
          'l/m',
        )}`,
      ),
  })

  // Add Pipe
  const { mutate: addPipe, isLoading: loadingAddPipe } =
    useAddHydraulic(parentId)

  //Update Pipe
  const { data, isFetching } = useSearchHydraulicId(interfaceId, Number(id))
  const { mutate: updatePipe, isLoading: loadingUpdatePipe } =
    useEditHydraulic(parentId)

  const form = useForm<FormPipe>({
    resolver: yupResolver(schema),
  })
  const { reset, handleSubmit } = form

  const roundDownToOneDecimalPlace = (num: number) => {
    return Math.floor(num)
  }

  const handleSave = (data: FormPipe) => {
    const capacity = isMetric
      ? data.capacity
      : convertToMetricV2(data.capacity, 'l/m', 'gpm', 2)

    if (id) {
      updatePipe({
        interfaceId: interfaceId,
        id: id,
        parentId: data.parentId,
        description: data.description,
        currentFlow: 0,
        localFlow: 0,
        capacity,
      })
    } else {
      addPipe({
        interfaceId: interfaceId,
        parentId: parentId ? parentId : 0,
        description: data.description,
        capacity,
      })
    }
  }

  useEffect(() => {
    if (inputRefSiteName.current) {
      inputRefSiteName.current.focus()
    }
  }, [])

  useEffect(() => {
    if (id && data) {
      reset({
        id: data.id,
        description: data.description,
        capacity: convertFromMetricV2(data.capacity, 'l/m', 'gpm', 2),
        parentId: data.parentId,
      })
    } else {
      reset({
        id: 0,
        interfaceId: interfaceId,
        description: '',
        parentId: parentId,
        capacity: 0,
      })
    }

    //eslint-disable-next-line
  }, [data, id])

  return (
    <Container className={classes.container}>
      {isFetching && (
        <Stack
          width="377px"
          height="100%"
          justifyContent="center"
          alignItems="center"
        >
          <CircularProgress />
        </Stack>
      )}
      {!isFetching && (
        <Fade in={!isFetching}>
          <form onSubmit={handleSubmit(handleSave)}>
            <Stack className={classes.formEdit}>
              <Stack className={classes.wrapper}>
                <Typography className={classes.editTitlePipe}>
                  {!id && !parentId && t('commonActionAddNewPipe')}
                  {!!id && !parentId && t('EditPipe')}
                  {!id && !!parentId && t('AddSubPipe')}
                  {!!id && !!parentId && t('EditSubPipe')}
                </Typography>
                <Stack direction="row">
                  <Button
                    variant="secondary"
                    size="small"
                    className={classes.btnCancel}
                    onClick={() => closeSidebar()}
                  >
                    {t('commonActionsCancel')}
                  </Button>
                  <LoadingButton
                    type="submit"
                    size="small"
                    variant="primary"
                    className={classes.loadingBtn}
                    loading={id ? loadingUpdatePipe : loadingAddPipe}
                  >
                    {t('commonActionsSave')}
                  </LoadingButton>
                </Stack>
              </Stack>

              <Stack>
                {!!id && (
                  <InputLabelField
                    name="id"
                    form={form}
                    disabled={true}
                    label="ID"
                    placeholder="ID"
                    style={{
                      height: 50,
                      borderRadius: '4px',
                      width: { xs: '377px' },
                      marginTop: '8px',
                    }}
                    padding="8px 16px"
                  />
                )}

                {!!parentId && (
                  <InputLabelField
                    name="parentId"
                    form={form}
                    disabled={true}
                    label={t('formsLabelParentID')}
                    placeholder={t('formsLabelParentID')}
                    style={{
                      height: 50,
                      borderRadius: '4px',
                      width: { xs: '377px' },
                      marginTop: '8px',
                    }}
                    padding="8px 16px"
                  />
                )}

                <InputLabelField
                  isRequired
                  name="description"
                  inputRef={inputRefSiteName}
                  form={form}
                  label={t('formsLabelsDescription')}
                  placeholder={t('formsLabelsDescription')}
                  style={{
                    height: 50,
                    borderRadius: '4px',
                    width: { xs: '377px' },
                    marginTop: '8px',
                  }}
                  padding="8px 16px"
                />
                <InputLabelField
                  name="capacity"
                  form={form}
                  label={`${t('formsLabelsCapacity')} (${convertLabelUnit('l/m')})`}
                  placeholder="Capacity"
                  style={{
                    height: 50,
                    borderRadius: '4px',
                    width: { xs: '377px' },
                    marginTop: '8px',
                  }}
                  padding="8px 16px"
                />
              </Stack>
            </Stack>
          </form>
        </Fade>
      )}
    </Container>
  )
}

export default FormHydraulicView
